import React, { useState, useEffect, useContext } from 'react'

import { DevToolsContext } from 'contexts'

import { ShoppingBagIcon } from 'lucide-react'

import { useTheme, Typography } from '@mui/material'

import { Alert } from '@repo/ui'
import {
  EmptyState,
  Header,
  Table,
} from 'components'

import { useReferrerState } from 'hooks'
import { useShopifyOrders } from 'hooks/api/useShopifyOrdersQuery'
import { useOrdersStore } from 'store/orders/ordersStore'

import { useOrderColumns } from './useOrderColumns'
import { useOrderRowActions } from './useOrderRowActions'

export const OrdersPage = () => {
  const theme = useTheme()
  // Referrer state handed to the order drill-in so its Header offers "← Back to Orders".
  const referrer = useReferrerState()
  const [errorDismissed, setErrorDismissed] = useState(false)

  // Zustand store for UI state
  const {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize
  } = useOrdersStore()

  // DevToolsContext is created with `createContext(null)` (untyped), so the
  // consumer shape has to be asserted locally rather than inferred.
  const devTools = useContext(DevToolsContext) as { devToolsEnabled?: boolean } | null
  const devToolsEnabled = devTools?.devToolsEnabled ?? false

  // Calculate offset for pagination
  const offset = (currentPage - 1) * pageSize

  // React Query hooks
  const {
    data: shopifyOrdersData,
    isLoading,
    isError,
    error,
    refetch,
  } = useShopifyOrders({
    limit: pageSize,
    offset,
  })

  // Reset the dismissed-error flag once the underlying error clears, so a
  // future error shows the alert again instead of staying hidden forever.
  useEffect(() => {
    if (!isError) setErrorDismissed(false)
  }, [isError])

  const orders = shopifyOrdersData?.orders ?? []
  const total = shopifyOrdersData?.total ?? 0

  /*
   * VRG-512 (§71): the row actions, their gating, the modals they drive, and the
   * column definitions all live in `./useOrderRowActions` + `./useOrderColumns`, so
   * the company detail page's Orders tab renders the same table rather than its own
   * four-column re-declaration. This page keeps what is genuinely its own: the
   * Header, the error alert, server pagination, and `fillBottom` (§22).
   */
  const { getActionItems, getBulkActions, modals } = useOrderRowActions({ referrer, refetch })
  const columns = useOrderColumns({ referrer, getActionItems })

  return (
    <>
      <Header
        title="Orders"
        rightComponents={
          // Package assignment happens per-order (row kebab "Assign Packages")
          // and in bulk (the 1/N flow) — no order-agnostic header CTA (it was a
          // no-op with no order to target).
          devToolsEnabled ? (
            <Typography variant="body2" color={theme.palette.brand[500]} sx={{ fontStyle: 'italic' }}>
              Development Mode - Using Draft Orders
            </Typography>
          ) : undefined
        }
      />

      {/* Error Alert */}
      {isError && !errorDismissed && (
        <Alert
          severity="error"
          title="Error loading orders"
          description={error?.message || 'Unknown error occurred'}
          onClose={() => setErrorDismissed(true)}
        />
      )}

      {/*
        VRG-512 — NO `IntroDataBanner` here, deliberately. This is `/internal/*`, the
        Vera-staff cross-company console, and no sample/intro data is to surface under
        it. A caveat saying "these include sample data" only makes sense on a surface
        that is allowed to show samples; on a staff view the right answer is that there
        is nothing to caveat. The banner stays on the eight BRAND-facing surfaces.
      */}

      <Table
        fillBottom
        loading={isLoading}
        columns={columns}
        rows={orders}
        bulkActions={getBulkActions}
        alwaysShowRowSelection={false}
        paginationMode="server"
        rowCount={total}
        paginationModel={{ page: currentPage - 1, pageSize }}
        onPaginationModelChange={(model) => {
          setCurrentPage(model.page + 1)
          setPageSize(model.pageSize)
        }}
        emptyState={
          <EmptyState
            title="No orders yet"
            text="Orders placed through the Tag Shop will show up here."
            icon={<ShoppingBagIcon strokeWidth={1} size={32} />}
          />
        }
      />

      {/* The Assign / Buy-Label modals the kebab and bulk bar drive (§71 — shared
          with the company detail page's Orders tab). Inert without this render. */}
      {modals}
    </>
  )
}
